import merge from "lodash/merge";
import {
  Context,
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { usePortfolioContextEffects } from "./hooks/usePortfolioContextEffects";
import { portfolioReducer } from "./reducer";
import { IPortfolioContext, IPortfolioState } from "./types";

export const defaultPortfolioState: IPortfolioState = {
  derived: { activeTabTitle: undefined },
  farms: {
    isFetchingFarms: false,
    isFetchedFarms: false,
    history: undefined,
    positions: undefined,
  },
  orders: {
    allOrders: undefined,
    formattedOrders: undefined,
    isFetchedOps: false,
    isFetchingOps: false,
    isRefetchingOps: false,
    opsHistory: undefined,
    opsOpen: undefined,
    filters: { orderStatus: {} },
    search: { value: undefined },
    ordersTableColumnWidths: undefined,
    ordersTableHeaders: undefined,
  },
  tabs: {
    assets: {
      selectedAsset: undefined,
    },
  },
};

const PortfolioContextContextEffects: FC<PropsWithChildren> = ({ children }) => {
  // Effects that apply only to the nearest PortfolioContext.
  usePortfolioContextEffects();

  return <>{children}</>;
};

const PortfolioContext: Context<IPortfolioContext> = createContext({
  state: defaultPortfolioState,
  dispatch: (_val) => {},
});

export const PortfolioContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(portfolioReducer, merge({}, defaultPortfolioState));
  const memoizedDispatch = useMemo(() => dispatch, []);

  return (
    <PortfolioContext.Provider value={{ state, dispatch: memoizedDispatch }}>
      <PortfolioContextContextEffects>{children}</PortfolioContextContextEffects>
    </PortfolioContext.Provider>
  );
};

// Utility hook.
export const usePortfolioContext = (): IPortfolioContext => useContext(PortfolioContext);
